import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  ViewRef
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextModule, I18NextPipe } from 'angular-i18next';
import { cloneDeep, isNil } from 'lodash';
import { CheckboxComponent, MbsPopupType, MbsUiKitModule, MbsValidators, ModalService, ModalSettings } from 'mbs-ui-kit';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { NotificationPlanRule, NotificationRuleSettings } from 'mbs-ui-shared@models';
import { ThresholdUnitsType } from 'mbs-ui-shared@models/Notification';
import { hasNotificationPlanRule } from 'mbs-ui-shared@utils/notification';
import { noop, Subject } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { thresholdUnits } from './notifications-checkboxes.constants';

export const NOTIFICATIONS_CHECKBOXES_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => NotificationsCheckboxesComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-notifications-checkboxes',
  templateUrl: './notifications-checkboxes.component.html',
  styleUrls: ['./notifications-checkboxes.component.scss'],
  imports: [CommonModule, MbsUiKitModule, FormsModule, I18NextModule, NgbTooltipModule],
  providers: [NOTIFICATIONS_CHECKBOXES_VALUE_ACCESSOR],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationsCheckboxesComponent implements ControlValueAccessor {
  public readonly alertType = MbsPopupType;
  public readonly validatorEmailsMessage = this.i18N.transform('shared:validation.noEmail');
  public readonly thresholdDefaultMinValue = 1;
  public readonly thresholdEnabledFailedMinValue = 2;
  public readonly thresholdMaxValue = 99;
  public isNil = isNil;
  @ViewChild('specifiedEmails', { static: true }) specifiedEmailsRef: CheckboxComponent;
  @Output() isValidControl = new EventEmitter<boolean>();
  @Input() public disabled = false;
  public get disableState(): boolean {
    return !(this.disabled === null || this.disabled === undefined) && this.disabled !== false;
  }
  @Input() prefixId: string;
  @Input() addAdmin = true;
  @Input() addEmail = true;
  @Input() addThresholdFailed = false;
  @Input() addThresholdNoSuccess = false;
  @Input() cardMode = false;
  @Input() readOnly = false;
  @Input() hideThresholdM365Tooltip = false;

  private myPermissions: NotificationRuleSettings = new NotificationRuleSettings();
  @Input() set permissions(value: NotificationRuleSettings) {
    this.myPermissions = value;
    this.notifyValueChange();
  }

  get permissions(): NotificationRuleSettings {
    return this.myPermissions;
  }

  get thresholdMinValue(): number {
    return this.permissions.threshold && this.addThresholdFailed ? this.thresholdEnabledFailedMinValue : this.thresholdDefaultMinValue;
  }

  get allEmails(): string {
    const emails = (this.providerEmail && this.handleGetPermission(NotificationPlanRule.Provider) ? [this.providerEmail] : [])
      .concat(this.handleGetPermission(NotificationPlanRule.SpecifiedEmails) ? this.myPermissions?.emails || [] : [])
      .join(', ');

    return emails || this.i18N.transform('shared:notifications.noEmails');
  }

  public providerEmail: string;
  public thresholdUnits = thresholdUnits(this.i18N);

  public get thresholdEnabled(): boolean {
    return (
      (this.handleGetPermission(NotificationPlanRule.Provider) || this.handleGetPermission(NotificationPlanRule.SpecifiedEmails)) &&
      ((this.addThresholdFailed && !isNil(this.permissions.threshold)) || this.addThresholdNoSuccess)
    );
  }

  public get thresholdUnitText(): string {
    return this.i18N.transform(
      (this.permissions?.thresholdUnit || 'days') === 'days'
        ? 'shared:notifications.thresholdUnitDay'
        : 'shared:notifications.thresholdUnitHour',
      { count: this.permissions?.threshold }
    );
  }

  public rule = NotificationPlanRule;
  public emailValidator = (control: AbstractControl) => (control.value ? MbsValidators.emailValidator(control) : null);
  private disabledApply$: Subject<boolean> = new Subject();
  public editData: any[];
  private oldData: any[];

  constructor(private auth: AuthFacade, private modalService: ModalService, private cd: ChangeDetectorRef, private i18N: I18NextPipe) {
    this.auth.currentUser$
      .pipe(
        map((user) => user?.ProviderInfo?.EmailNotificationsTo),
        untilDestroyed(this)
      )
      .subscribe((email) => {
        this.providerEmail = email;
      });
  }

  onChange: (value) => {};
  onTouched: () => {};

  notifyValueChange(): void {
    if (this.onChange) {
      this.onChange(this.permissions);
    }
  }

  writeValue(obj: NotificationRuleSettings): void {
    if (!obj) return;

    // Store readonly fix
    const newObj = cloneDeep(obj);

    if (!Array.isArray(newObj?.emails)) {
      newObj.emails = [];
    }
    this.myPermissions = newObj || new NotificationRuleSettings();
    this.detectChangesRef();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  // TODO implement
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = this.disableState;
    this.detectChangesRef();
  }

  handleGetPermission(rule: NotificationPlanRule): boolean {
    return hasNotificationPlanRule(this.permissions.rule, rule);
  }

  handleSetPermission(rule: NotificationPlanRule): void {
    this.permissions.rule ^= rule;
    !this.handleGetPermission(NotificationPlanRule.Provider) &&
      !this.handleGetPermission(NotificationPlanRule.SpecifiedEmails) &&
      (this.permissions.threshold = null);
    this.notifyValueChange();
  }

  handleAddNew(modal: TemplateRef<any>, data: any[]): void {
    const modalSettings = new ModalSettings();
    modalSettings.header = {
      title: this.i18N.transform('shared:notifications.addSpecifiedEmails')
    };
    modalSettings.footer = {
      okButton: {
        text: this.i18N.transform('shared:buttons.apply'),
        disabled$: this.disabledApply$.asObservable().pipe(startWith(true))
      }
    };

    this.editData = Array.from(data);
    this.oldData = Array.from(data);

    this.modalService.open(modalSettings, modal).then(() => {
      data.splice(0); // save ref to array
      data.push(...this.editData);
      if (this.oldData.length === 0 && data.length !== 0 && [] && !this.handleGetPermission(NotificationPlanRule.SpecifiedEmails)) {
        this.handleSetPermission(NotificationPlanRule.SpecifiedEmails);
      } else {
        this.notifyValueChange();
      }
      this.detectChangesRef();
    }, noop);
  }

  handleListChange(newData: Set<any>): void {
    this.disabledApply$.next(!this.isListChanged(this.oldData, Array.from(newData)));
  }

  isListChanged(oldData, newData): boolean {
    if (oldData.length !== newData.length) return true;
    let found = false;
    for (const oldItem of oldData) {
      found = newData.some((newItem) => newItem === oldItem);
      if (!found) return true;
    }
    return false;
  }

  detectChangesRef(): void {
    !(this.cd as ViewRef).destroyed && this.cd.detectChanges();
  }

  handleEnableThreshold(value: boolean): void {
    if (value)
      return this.handleSetThresholdValue(this.addThresholdFailed ? this.thresholdEnabledFailedMinValue : this.thresholdDefaultMinValue);

    this.permissions.threshold = null;
    this.notifyValueChange();
  }

  handleSetThresholdValue(value: number): void {
    this.permissions.threshold = value;
    this.notifyValueChange();
  }

  handleSetThresholdUnit(value: string): void {
    this.permissions.thresholdUnit = value as ThresholdUnitsType;
    this.notifyValueChange();
  }

  thresholdTooltip(): string {
    if (!this.addThresholdFailed) return this.i18N.transform('shared:notifications.noSuccessThresholdTooltip');

    return this.i18N
      .transform('shared:notifications.failedThresholdTooltip')
      .concat(this.hideThresholdM365Tooltip ? '' : this.i18N.transform('shared:notifications.failedThresholdTooltip2'));
  }
}
